import { Form } from 'antd';
import { useRef, useState } from 'react';

import { useItemMedia, useUnitMedia } from '@/api/masters';
import { uploadItemMedia, uploadUnitMedia } from '@/api/upload';
import { ApiErrorAlert } from '@/components/ApiErrorAlert';
import { FormDrawer } from '@/components/FormDrawer';
import { Button, EmptyState, Notice } from '@/components/PageHeading';
import { WriteAction } from '@/components/WriteAction';
import type { Session } from '@core/session/types';

import { MediaGallery, ProgressRow } from './MediaGallery';
import { formatSize, galleryOf, type MediaScope } from './mediaScope';
import { acceptedTypes } from './items/designForm';

/** One file on its way up, while it is still going. */
type InFlight = {
  id: string;
  name: string;
  fraction: number;
};

/**
 * Photographs and clips, for a design or for one physical garment.
 *
 * <p>
 * <strong>The files never pass through our API.</strong> Each is announced to the server, PUT
 * straight to the object store from this browser, then confirmed — which is why there is a progress
 * bar per file rather than a spinner over the drawer: a hundred-megabyte clip is a real wait. It is
 * also why closing mid-upload leaves nothing in the gallery: no row exists until the bytes land.
 * </p>
 *
 * <p>
 * The URLs are presigned and expire in minutes. They are rendered, never stored, and the query
 * refetches on focus — a tab left open over lunch would otherwise come back to broken images.
 * </p>
 *
 * <p>
 * The grid itself is `MediaGallery`, shared with the design page.
 * </p>
 */
export function MediaDrawer({
  session,
  scope,
  onClose,
}: {
  session: Session;
  scope: MediaScope;
  onClose: () => void;
}) {
  const [form] = Form.useForm<Record<string, never>>();
  const [inFlight, setInFlight] = useState<InFlight[]>([]);
  const [uploadError, setUploadError] = useState<string | null>(null);

  const input = useRef<HTMLInputElement>(null);

  const isUnit = scope.level === 'unit';
  const { permission } = galleryOf(scope);

  const itemMedia = useItemMedia(isUnit ? undefined : scope.itemId);
  const unitMedia = useUnitMedia(isUnit ? scope.unitId : undefined);

  const media = isUnit ? unitMedia : itemMedia;

  async function send(files: FileList) {
    setUploadError(null);

    // Sequentially rather than all at once. A shop's upstream is the bottleneck, and six parallel
    // uploads on a shop connection means six bars that all crawl — one at a time finishes the first
    // photograph while the others wait, which is what somebody watching actually wants.
    for (const file of Array.from(files)) {
      const id = `${file.name}-${Date.now()}-${Math.random()}`;

      setInFlight((current) => [...current, { id, name: file.name, fraction: 0 }]);

      try {
        const report = (fraction: number) =>
          setInFlight((current) =>
            current.map((entry) => (entry.id === id ? { ...entry, fraction } : entry)),
          );

        if (isUnit) {
          await uploadUnitMedia(scope.unitId, file, report);
        } else {
          await uploadItemMedia(scope.itemId, file, report);
        }

        await media.refetch();
      } catch (error) {
        // Named per file: "three of five failed" is not something a shop can act on, and the two
        // that worked are already in the gallery.
        setUploadError(
          `${file.name}: ${error instanceof Error ? error.message : 'could not be uploaded.'}`,
        );
      } finally {
        setInFlight((current) => current.filter((entry) => entry.id !== id));
      }
    }
  }

  const rows = media.data ?? [];

  return (
    <FormDrawer<Record<string, never>>
      title="Photographs and clips"
      subtitle={isUnit ? `${scope.name} — this garment only` : scope.name}
      open
      onClose={onClose}
      form={form}
      onSubmit={() => onClose()}
      submitText="Done"
      width={620}
    >
      {media.isError && <ApiErrorAlert error={media.error} />}

      {uploadError && (
        <Notice tone="danger" title="An upload did not finish">
          {uploadError}
        </Notice>
      )}

      {isUnit && (
        <Notice tone="info" title="This garment, not the design">
          These are pictures of this one physical garment — what it looks like now, marks and all.
          The booking screens show one of these where it exists and fall back to the design's
          catalogue photograph otherwise.
        </Notice>
      )}

      <WriteAction session={session} required={permission}>
        {(disabled) => (
          <div className="mb-4">
            <input
              ref={input}
              type="file"
              // Both kinds in one picker, and `multiple` because a garment is photographed from
              // several angles in one sitting.
              accept={acceptedTypes}
              multiple
              className="hidden"
              onChange={(event) => {
                if (event.target.files && event.target.files.length > 0) {
                  void send(event.target.files);
                }

                // Cleared so choosing the same file twice in a row still fires a change.
                event.target.value = '';
              }}
            />

            <Button
              variant="primary"
              icon="icon icon-upload"
              disabled={disabled || inFlight.length > 0}
              onClick={() => input.current?.click()}
            >
              {inFlight.length > 0 ? 'Uploading…' : 'Add photographs or a clip'}
            </Button>

            <p className="text-default mt-2 mb-0 text-[12px]">
              JPEG, PNG, WebP or HEIC up to 10 MB each; MP4, WebM or MOV up to 200 MB.
            </p>
          </div>
        )}
      </WriteAction>

      {inFlight.length > 0 && (
        <div className="mb-4 flex flex-col gap-2">
          {inFlight.map((entry) => (
            <ProgressRow key={entry.id} name={entry.name} fraction={entry.fraction} />
          ))}
        </div>
      )}

      {rows.length === 0 && inFlight.length === 0 ? (
        <EmptyState
          icon="icon icon-image-off"
          title="Nothing uploaded yet"
          hint={
            isUnit
              ? 'Add a photograph of this garment and the booking screens will show it instead of the design’s.'
              : 'Add a photograph and it becomes this design’s main picture.'
          }
        />
      ) : (
        <MediaGallery session={session} scope={scope} rows={rows} />
      )}

      {rows.length > 0 && (
        <p className="text-default mt-4 mb-0 text-[12px]">
          {rows.length} file{rows.length === 1 ? '' : 's'} ·{' '}
          {formatSize(rows.reduce((sum, row) => sum + row.sizeBytes, 0))}
        </p>
      )}
    </FormDrawer>
  );
}
